import { useState } from 'react'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Dropzone,
  DropzoneRejectionCode,
  Flex,
  FormField,
  Logo,
  theme
} from '@percent/lemonade'
import { ReactComponent as PlusIcon } from '@percent/cause-dashboard/common/assets/images/plus-icon.svg'
import { useMutation } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import { PRIVACY_POLICY_LINK, TERMS_LINK } from '@percent/cause-dashboard/constants/legal'
import { ValidationRequestDocumentStatusResponse } from '@percent/cause-dashboard/services/validationRequestDocuments/ValidationRequestDocuments.types'
import styles from '../agentVerificationDocuments/AgentVerificationDocuments.module.scss'
import pageStyles from './ValidationRequestDocuments.module.scss'

const MAX_FILES = 5
const MAX_FILE_SIZE_IN_MB = 5
const DOCUMENT_TYPES = ['registration', 'governingDocument', 'charitableStatus'] as const

const validateFileSize = (files?: File[] | null) =>
  files ? files.every(file => file.size <= MAX_FILE_SIZE_IN_MB * 1024 * 1024) : true

type ValidationRequestDocumentsFormValues = { files: File[]; termsAndConditions: boolean }

const validationSchema = Yup.object().shape({
  files: Yup.array()
    .min(1, '')
    .max(MAX_FILES, 'errorMessage.maxFilesLimit')
    .test('validate-file-size', 'errorMessage.maxFileSize', validateFileSize),
  termsAndConditions: Yup.bool().oneOf([true])
})

type ValidationRequestDocumentsFormProps = {
  validationRequestToken: string
  status: ValidationRequestDocumentStatusResponse
  onDocumentsSubmitted: () => void
  onUploadClosed: () => void
}

const UPLOAD_CLOSED_ERROR_CODES = [
  'validation_request_document_upload/invalid_token',
  'validation_request_document_upload/not_awaiting_documents'
]

export const ValidationRequestDocumentsForm = ({
  validationRequestToken,
  status,
  onDocumentsSubmitted,
  onUploadClosed
}: Readonly<ValidationRequestDocumentsFormProps>) => {
  const [hasError, setHasError] = useState(false)
  const { t } = useTranslation()
  const { validationRequestDocumentsService } = useServices()

  const [{ isLoading }, { apiFunc: submitDocuments }] = useMutation(
    validationRequestDocumentsService.postValidationRequestDocuments,
    () => onDocumentsSubmitted(),
    error => {
      // Only these mean retrying can't succeed; a refused file, also a 400, can be picked again.
      if (isAxiosError(error) && UPLOAD_CLOSED_ERROR_CODES.includes(error.response?.data?.error?.code)) {
        onUploadClosed()
      } else {
        setHasError(true)
      }
    }
  )

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<ValidationRequestDocumentsFormValues>({
    mode: 'onChange',
    defaultValues: { files: [], termsAndConditions: false },
    resolver: yupResolver(validationSchema)
  })

  const submitForm = handleSubmit(({ files }) => {
    const formData = new FormData()
    files.forEach((file, index) => formData.append(`file${index}`, file))
    submitDocuments({ validationRequestToken, formData })
  })

  return (
    <div className={styles.documentsContainer} data-testid="validation-request-documents-form">
      <div className={styles.cardHeader}>
        {status.partnerLogo && (
          <Flex align="center" gap={theme.spacing.m} data-testid="validation-request-documents-logos">
            <img
              className={pageStyles.partnerLogo}
              src={status.partnerLogo}
              alt={t('validationRequestDocuments.partnerLogoAlt', { partnerName: status.partnerName ?? '' })}
            />
            <PlusIcon className={pageStyles.plusIcon} />
            <Logo />
          </Flex>
        )}
        <h5>{t('validationRequestDocuments.title')}</h5>
      </div>

      <div className={styles.scrollableContent}>
        {hasError && (
          <Alert variant="error" title={t('agentVerificationDocuments.errorMessage.title')}>
            {t('agentVerificationDocuments.errorMessage.description')}{' '}
            <Anchor href={HELP_LINK} target="_blank" rel="noreferrer" fz="inherit">
              {t('agentVerificationDocuments.errorMessage.helpCenter')}
            </Anchor>
          </Alert>
        )}

        <p>{t('validationRequestDocuments.intro', { organisationName: status.organisationName })}</p>

        <Flex direction="column" gap={theme.spacing.xs}>
          <span className={pageStyles.documentTypesLabel}>{t('validationRequestDocuments.documentTypes.label')}</span>
          <ul
            style={{
              margin: 0,
              paddingLeft: theme.spacing.xl,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing.xxs
            }}
          >
            {DOCUMENT_TYPES.map(documentType => (
              <li key={documentType}>{t(`validationRequestDocuments.documentTypes.${documentType}`)}</li>
            ))}
          </ul>
        </Flex>

        <p>{t('validationRequestDocuments.note')}</p>

        <div className={styles.fileInputWrapper}>
          <FormField
            status={errors.files ? 'danger' : 'default'}
            statusMessage={errors.files?.message && t(errors.files.message as LocaleKey)}
          >
            <Controller
              name="files"
              control={control}
              render={({ field }) => (
                <Dropzone
                  title={t('validationRequestDocuments.dropzone.title')}
                  description={t('validationRequestDocuments.dropzone.description', {
                    maxFiles: MAX_FILES,
                    maxSize: MAX_FILE_SIZE_IN_MB
                  })}
                  files={field.value}
                  onChange={field.onChange}
                  maxFiles={MAX_FILES}
                  maxSize={MAX_FILE_SIZE_IN_MB * 1024 * 1024}
                  testId="documentation-dropzone"
                  getErrorMessage={({ fileName, code, maxSizeFormatted, acceptedFileTypes }) => {
                    const messages: Record<DropzoneRejectionCode, string> = {
                      'file-too-large': t('agentVerificationDocuments.reupload.dropzone.errors.fileTooLarge', {
                        fileName,
                        maxSize: maxSizeFormatted
                      }),
                      'file-invalid-type': t('agentVerificationDocuments.reupload.dropzone.errors.fileInvalidType', {
                        fileName,
                        fileTypes: acceptedFileTypes
                      }),
                      'too-many-files': t('agentVerificationDocuments.reupload.dropzone.errors.tooManyFiles', {
                        maxFiles: MAX_FILES
                      })
                    }

                    return messages[code]
                  }}
                />
              )}
            />
          </FormField>
        </div>

        <div className={styles.termsAndConditionsWrapper}>
          <p>{t('agentVerificationDocuments.acceptTerms')}</p>
          <Controller
            name="termsAndConditions"
            control={control}
            render={({ field }) => (
              <Checkbox
                active={field.value}
                variant="default"
                label={
                  <span>
                    {t('typography.agreeToThe')}{' '}
                    <Anchor href={TERMS_LINK} target="_blank" rel="noreferrer" fz="inherit">
                      {t('typography.terms')}
                    </Anchor>
                    {' & '}
                    <Anchor href={PRIVACY_POLICY_LINK} target="_blank" rel="noreferrer" fz="inherit">
                      {t('typography.privacyPolicy')}
                    </Anchor>
                  </span>
                }
                name="termsAndConditions"
                dataTestId="terms-and-conditions-checkbox"
                onChange={() => field.onChange(!field.value)}
              />
            )}
          />
        </div>
      </div>

      <div className={styles.submitButtonWrapper}>
        <div className={styles.submitButton}>
          <Button
            onClick={submitForm}
            loading={isLoading}
            disabled={!isValid || isLoading}
            data-testid="upload-documentation-button"
            stretch
          >
            {t('agentVerificationDocuments.submitDocumentation')}
          </Button>
        </div>
      </div>
    </div>
  )
}
